<template>
  <div class="relative inline-flex flex-col items-stretch">
    <div class="relative inline-flex flex-1 items-stretch">
      <svg
        class="w-2 h-2 absolute top-0 right-0 m-4 pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 412 232"
      >
        <path
          d="M206 171.144L42.678 7.822c-9.763-9.763-25.592-9.763-35.355 0-9.763 9.764-9.763 25.592 0 35.355l181 181c4.88 4.882 11.279 7.323 17.677 7.323s12.796-2.441 17.678-7.322l181-181c9.763-9.764 9.763-25.592 0-35.355-9.763-9.763-25.592-9.763-35.355 0L206 171.144z"
          fill="#648299"
          fill-rule="nonzero"
        />
      </svg>
      <select
        :id="id"
        class="h-full w-full border-2 rounded-lg
          pl-5 pr-10 bg-white
            focus:outline-none
            appearance-none"
        :style="{
          '--focus-border-color': error ? '#D20000' : global.primaryColor,
          color: boxColor,
          'border-color': error ? '#D20000' : defaultBorderColor
        }"
        :value="modelValue"
        :aria-label="$attrs['aria-label']"
        :aria-invalid="error ? 'true' : $attrs['aria-invalid']"
        :aria-describedby="inputDescribedBy"
        :aria-required="$attrs['aria-required']"
        :required="hasRequiredAttribute"
        @change="$emit('update:modelValue', $event.target.value)"
        @mousedown="handleFirstClick()"
      >
        <option
          style="display:none;"
          selected
        >
          {{ defaultSelectorText }}
        </option>
        <option
          v-for="(option, index) in options"
          :key="index"
          :selected="option===defaultSelectorValue"
        >
          {{ option }}
        </option>
      </select>
    </div>
    <p
      v-if="error && visibleErrorMessage"
      :id="id ? `${id}-error` : undefined"
      class="input-error-message"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      {{ visibleErrorMessage }}
    </p>
    <div
      :id="statusMessageId"
      class="sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ statusAnnouncement }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { mapGetters } from 'vuex';
import { hexToRgba } from '@/utils';

export default defineComponent({
  name: 'Select',
  props: {
    modelValue: {
      type: String,
      required: true
    },
    id: {
      type: String,
      default: ''
    },
    defaultSelectorText: {
      type: String,
      required: true
    },
    options: {
      type: Array as PropType<Array<string>>,
      required: true
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    valid: {
      type: Boolean,
      default: false
    },
    defaultSelectorValue: {
      type: String,
      default: undefined,
      required: false
    }
  },
  computed: {
    ...mapGetters([
      'global'
    ]),
    hasRequiredAttribute (): boolean {
      return this.$attrs.required !== undefined ||
        this.$attrs['aria-required'] === true ||
        this.$attrs['aria-required'] === 'true';
    },
    boxColor (): string {
      return this.global.boxColor || '#696E79';
    },
    hasValue (): boolean {
      return !!this.modelValue && this.modelValue !== this.defaultSelectorText;
    },
    defaultBorderColor (): string {
      return this.hasValue
        ? this.boxColor
        : hexToRgba(this.boxColor, 0.5);
    },
    inputDescribedBy (): string | undefined {
      const ids: string[] = [];
      const describedBy = this.$attrs['aria-describedby'];

      if (typeof describedBy === 'string' && describedBy.trim()) {
        ids.push(describedBy.trim());
      }

      if (this.statusAnnouncement && this.statusMessageId) {
        ids.push(this.statusMessageId);
      }

      if (this.error && this.errorMessage && this.id) {
        ids.push(`${this.id}-error`);
      }

      return ids.length ? ids.join(' ') : undefined;
    },
    fieldLabelText (): string {
      const ariaLabel = this.$attrs['aria-label'];

      if (typeof ariaLabel === 'string' && ariaLabel.trim()) {
        return ariaLabel.trim();
      }

      return this.defaultSelectorText.replace(/\*/g, '').trim();
    },
    statusAnnouncement (): string {
      if (!this.valid || !this.modelValue || this.error || !this.fieldLabelText) {
        return '';
      }

      return this.$t('message.accessibility.field_valid', {
        field: this.fieldLabelText
      }) as string;
    },
    statusMessageId (): string | undefined {
      return this.id ? `${this.id}-status` : undefined;
    },
    visibleErrorMessage (): string {
      if (!this.errorMessage) {
        return '';
      }

      return this.errorMessage.startsWith('* ')
        ? this.errorMessage
        : `* ${this.errorMessage}`;
    }
  },
  methods:{
    handleFirstClick(){
      if(!this.defaultSelectorValue)
        return;
      if(this.modelValue == this.defaultSelectorText){
        this.$emit('update:modelValue', this.defaultSelectorValue);
      }
    }
  }
});
</script>

<style>
select:focus {
  border-color: var(--focus-border-color) !important;
}

.input-error-message {
  margin: 0.25rem 0 0 0;
  font-size: 0.75rem;
  color: #D20000;
  font-weight: 700;
  line-height: 1.25;
  text-align: left;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
